import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { useCart } from '@printdeal/checkout-frontend';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from '@gatsbyjs/reach-router';

import { Link, Button, Heading } from '@printdeal/ui-library';

import useSystemPages from '../../../hooks/useSystemPages';

import LoginForm from '../LoginForm';
import { messages } from '../../../translations/login';
import LostPasswordLink from '../LostPasswordLink';
import { Location } from '../../../types/Location';

interface ModalContentLoginProps {
  setModalView: (key: string) => void;
  setIsModalOpen?: (state: boolean) => void;
  queryParamsForRegistrationLink?: string;
  userType?: {
    isEnduser?: boolean;
    isReseller?: boolean;
  };
}

const ModalContentLogin = ({
  setModalView,
  setIsModalOpen,
  queryParamsForRegistrationLink = '',
  userType,
}: ModalContentLoginProps) => {
  const intl = useIntl();
  const location = useLocation() as Location;

  const { cart } = useCart();
  const [
    registrationPage,
    cartPage,
    homePage,
    deliveryPage,
    accountDashboard,
    customerProgramSignUpPage,
  ] = useSystemPages(
    'registration',
    'cart',
    'home',
    'delivery',
    'accountDashboard',
    'customerProgramsSignUp',
  );

  let redirectURL = accountDashboard.path;

  // If we're on the login page inside the cart, this will overwrite /nl/login with /nl/delivery.
  if (location.state?.redirectUrl) {
    redirectURL = location.state.redirectUrl;
  }

  // Overrides related to check out
  // - When people use the loginDrawer on the cart page AND they've got items in their cart.
  //   On 0 lineItems, it's better to stay on the same page, aka cart.
  //   (Current implementation redirects to accountDashboard.)
  // - When the redirectUrl was previously set to the deliveryPage
  //   This can happen when people navigate to the loginPage within the check out flow.
  if ((location.pathname === cartPage.path && cart && cart.lineItems.length > 0)
    || location.state?.redirectUrl === deliveryPage.path) {
    redirectURL = deliveryPage.path;
  }

  if (location.pathname === customerProgramSignUpPage.path) {
    redirectURL = location.pathname;
  }

  if (location.pathname === homePage.path && userType?.isEnduser) {
    redirectURL = homePage.path;
  }

  return (
    <div className="pd-flex pd-flex-col pd-gap-3">
      <p>
        <FormattedMessage id="login.slideout.description" defaultMessage="It's good to see you again. You can log in with email and password." />
      </p>
      <LoginForm
        setIsModalOpen={setIsModalOpen}
        useLoginSlideOut
        redirectUrlAfterLogin={redirectURL}
      />
      <LostPasswordLink linkLocation="slideout" handleResetPasswordLinkClick={() => setModalView('reset')} />
      <hr />
      <Heading as="h3" className="pd-text-dim-800">
        <FormattedMessage id="login.slideout.registerTitle" defaultMessage="Create an account" />
      </Heading>
      <p>
        <FormattedMessage
          id="login.slideout.registerExplanation"
          defaultMessage="You only need to register once to be able to order. After registering you can continue with your order."
        />
      </p>
      <Button
        as={GatsbyLink}
        to={`${registrationPage?.path}${queryParamsForRegistrationLink}`}
        state={{ redirectUrl: location.state?.redirectUrl || location.href }}
        variant="secondary"
        data-cy="createAccountButton"
        className="pd-w-full"
      >
        <FormattedMessage id="account.register" defaultMessage="Create Account" />
      </Button>
      <p className="pd-text-xs pd-leading-tight">
        <FormattedMessage
          id="account.privacy.learnmore"
          defaultMessage="Do you want to learn about how we handle customer data?"
        />
        {' '}
        <FormattedMessage
          id="account.privacy.policy"
          defaultMessage="See our {link}"
          values={{
            link: (
              <Link
                as={GatsbyLink}
                to={intl.formatMessage(messages.termsUrl)}
              >
                <FormattedMessage id="login.terms.text" defaultMessage="terms and conditions" />
              </Link>
            ),
          }}
        />
      </p>
    </div>
  );
};

export default ModalContentLogin;
